import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import Layout from "../Layout";
import useUserStore from "../../utils/storeApp";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import HeaderUploadStatus from "./HeaderUploadStatus";
import { OverlayPanel } from "primereact/overlaypanel";
import { UploadType } from "../../utils/enums";
import useDragScroll from "../../hooks/useDragScroll";

const UploadSummary = () => {
  const dt = useRef(null);
  const tableContainerRef = useRef(null);
  useDragScroll(tableContainerRef);
  const [userObj, setUserObj] = useState(null);
  const [gridObj, setGridObj] = useState([]);
  const navigate = useNavigate();
  const { sharedObject } = useUserStore((state) => ({
    sharedObject: state.sharedObject,
  }));
  const [globalFilter, setGlobalFilter] = useState(" ");
  const [validationNr, SetValidationNr] = useState([]);
  const [successNr, SetSuccessNr] = useState([]);
  const [obj, SetObj] = useState([]);
  const [objNr, SetObjNr] = useState([]);
  const [flag, setFlag] = useState(false);
  const op = useRef(null);
  const popoverClick = (event) => {
    op.current.toggle(event); // Show/hide the popover
  };
  const [expandedRows, setExpandedRows] = useState(null);
  const isRowExpanded = (row) => !!expandedRows?.[row._rowId];

  useEffect(() => {
    var obj = sharedObject.UploadStatusResult;
    if (Object.keys(obj).length > 0 && obj.constructor === Object) {
      setUserObj(obj);
      let parseGrid = JSON.parse(obj.report);
      SetObjNr(parseGrid);
      BuildUploadObj(parseGrid);
    }
  }, [sharedObject.UploadStatusResult]);
  const BuildUploadObj = async (obj) => {
    let successHolder = [];
    let errorHolder = [];
    let validationHolder = [];
    obj?.forEach((el) => {
      if (el.Status === "Success") {
        successHolder.push(el);
      } else if (el.Status === "FailedValidation") {
        validationHolder.push(el);
      } else {
        errorHolder.push(el);
      }
    });
    const combined = [...validationHolder, ...errorHolder].map((item, idx) => {
      const keyCandidate =
        item.UserName ||
        item.ExternalID ||
        item.ContactEmail ||
        item.NPI ||
        item.OrganizationName ||
        `${idx}`;
      return { ...item, _rowId: String(keyCandidate) };
    });

    setGridObj(combined);
    SetValidationNr(validationHolder);
    SetSuccessNr(successHolder);
    SetObj(obj.length);
  };
  const formatCreateDate = (dt) => {
    return dt ? new Date(dt).toLocaleString() : null;
  };
  const exportCSV = (selectionOnly) => {
    dt.current.exportCSV({ selectionOnly });
  };

  const getReasons = (row) => {
    let msg = row.ValidationMessage;
    try {
      if (!row?.ValidationMessage) return [];
      const parsed = JSON.parse(msg);
      return Array.isArray(parsed) ? parsed : [];
    } catch (es) {
      if (typeof msg === "string") {
        return [msg];
      }
      return [];
    }
  };

  const reasonCountBody = (rowData) => {
    const reasons = getReasons(rowData);
    const count = reasons.length;
    if (count === 0) return <span>—</span>;

    const expanded = isRowExpanded(rowData);

    return (
      <button
        type="button"
        className="btn btn-link p-0"
        aria-label={`${count} ${count === 1 ? "error" : "errors"} — click to ${expanded ? "collapse" : "expand"} details`}
        aria-expanded={expanded}
        onClick={() => toggleRow(rowData)}
      >
        {count} {count === 1 ? "error" : "errors"} {expanded ? "▴" : "▾"}
      </button>
    );
  };

  const toggleRow = (row) => {
    setExpandedRows((prev) => {
      const next = { ...(prev || {}) };
      if (next[row._rowId]) delete next[row._rowId];
      else next[row._rowId] = true;
      return next;
    });
  };
  const onRowToggle = (e) => setExpandedRows(e.data);
  const rowExpansionTemplate = (data) => {
    const reasons = getReasons(data);
    if (!reasons.length) return null;
    return (
      <div className="p-3">
        <strong>Reason{reasons.length > 1 ? "s" : ""}:</strong>
        <ul className="mt-2 mb-0">
          {reasons.map((msg, i) => (
            <li key={i}>{String(msg)}</li>
          ))}
        </ul>
      </div>
    );
  };
  useEffect(() => {
    if (flag) navigate(flag, { replace: true });
  }, [flag, navigate]);

  const getUploadTypeDisplay = (dt) => {
    switch (dt) {
      case UploadType.AddOrganizations:
        return "Organization";
      case UploadType.AddOrganizationEmails:
        return "Organization Email";
      case UploadType.DeleteOrganizationEmails:
        return "Delete Organization Email";
      case UploadType.AddUsers:
        return "Add User";
      case UploadType.DeleteUsers:
        return "Delete User";
      default:
        return;
    }
  };
  return (
    <Layout>
      <HeaderUploadStatus />
      <header>
        <div className="spacer-inline btnback-container-1">
          <button
            className="btn btn-outline-secondary btnback sp-pad0"
            aria-label="Go back to upload status list"
            type="button"
            onClick={() => setFlag("/upload-status")}
          >
            <i className="bi bi-arrow-left-square-fill" aria-hidden="true"></i>
          </button>
        </div>

        <h4 className="title-label3">Upload Results</h4>

        <div className="mid-header-bar spacer-inline">
          <label htmlFor="uploadSummarySearch" className="sr-only">Search upload results</label>
          <input
            id="uploadSummarySearch"
            className="form-control w-md2 ht-5 spacer-inline spacer-margin6 shade-2"
            type="search"
            aria-label="Search upload results"
            onInput={(e) => setGlobalFilter(e.target.value)}
            placeholder="Search"
          />
        </div>
      </header>
      <section aria-label="Upload file details">
        <dl className="flex1 header-result g-1">
          <div>
            <dt>Filename:</dt>
            <dd>{userObj?.fileName}</dd>
          </div>
          <div>
            <dt>Date Created:</dt>
            <dd>{formatCreateDate(userObj?.dateCreated)}</dd>
          </div>
          <div>
            <dt>Upload Type:</dt>
            <dd>{getUploadTypeDisplay(userObj?.uploadType)}</dd>
          </div>
          <div>
            <dt>Uploaded By:</dt>
            <dd>{userObj?.startedBy}</dd>
          </div>
          {userObj?.uploadType === UploadType.AddOrganizationEmails ||
          userObj?.uploadType === UploadType.DeleteOrganizationEmails ? (
            ""
          ) : (
            <div>
              <dt>Organization:</dt>
              <dd>{userObj?.organizationName}</dd>
            </div>
          )}
        </dl>
      </section>
      <section className="layout-container" aria-label="Upload results summary">
        <div className="grid-layout">
          <div className="field-wrapper v-top spacer-inline w-lg2">
           <dl>
            <div>
              <dt>
                {getUploadTypeDisplay(userObj?.uploadType)}
                {userObj?.uploadType === UploadType.AddUsers ||
                userObj?.uploadType === UploadType.DeleteUsers
                  ? ""
                  : "s"}{" "}
                requested:
              </dt>{" "}
              <dd>{obj}</dd>
            </div>
            <div>
              <dt>
                {getUploadTypeDisplay(userObj?.uploadType)}
                {userObj?.uploadType === UploadType.AddUsers ||
                userObj?.uploadType === UploadType.DeleteUsers
                  ? ""
                  : "s"}{" "}
                successfully{" "}
                {userObj?.uploadType === UploadType.DeleteUsers
                  ? "delete"
                  : "registered"}
                :
              </dt>{" "}
              <dd>{successNr.length}</dd>
            </div>
            <div>
              <dt>
                {getUploadTypeDisplay(userObj?.uploadType)}
                {userObj?.uploadType === UploadType.AddUsers ||
                userObj?.uploadType === UploadType.DeleteUsers
                  ? ""
                  : "s"}{" "}
                failed to{" "}
                {userObj?.uploadType === UploadType.DeleteUsers
                  ? "delete"
                  : "registered"}
                :{" "}
              </dt>
              <dd>{gridObj.length}</dd>
            </div>
            <div>
              <dt>
                {getUploadTypeDisplay(userObj?.uploadType)}
                {userObj?.uploadType === UploadType.AddUsers ||
                userObj?.uploadType === UploadType.DeleteUsers
                  ? ""
                  : "s"}{" "}
                failed validation:{" "}
              </dt>
              <dd>{validationNr.length}</dd>
            </div>
            </dl>
          </div>

          {gridObj.length > 0 ? (
            <div className="v-top spacer-inline w-lg9 ">
              <div className="alert-error text-size7" role="alert" aria-live="assertive">
                <i className="bi bi-exclamation-diamond text-size5" aria-hidden="true"></i>
                <span className="sr-only">Upload completed with errors.</span>
              </div>
              <div className="rd-clr ctr">
                The following accounts could not be completed due to errors.
                <br />
                Please identify the errors from below and make your changes.
              </div>
            </div>
          ) : objNr.length > 0 ? (
            <div className="v-top spacer-inline w-lg9 g-clr ctr">
              <i className="bi bi-check2-circle text-size5" aria-hidden="true"
              ></i>
              <h3 className="g-clr">
                All accounts have been successfully
                {userObj?.uploadType === UploadType.DeleteUsers
                  ? " deleted"
                  : " registered"}
                !
              </h3>
            </div>
          ) : (
            <div className="v-top spacer-inline w-lg9 text-warning ctr">
              <i className="bi bi-check2-circle text-size5" aria-hidden="true"></i>
              <h3 className="sp-mx4 text-warning">
                No records were found for the uploaded file.
              </h3>
            </div>
          )}

          <div>
            <h2 className="title-label4 rd-clr">Failed Summary</h2>
            <div className="spacer-inline txt-s10 float-right">
              <button
                type="button"
                className="btn btn-outline-dark sp-margin1 btn-reports-sm"
                aria-label="Download failed upload summary report as CSV file"
                onClick={() => exportCSV(false)}
              >
                <i className="bi bi-download" aria-hidden="true"></i>
              </button>
              <button
                type="button"
                className="btn btn-outline-dark sp-margin5 btn-reports-sm"
                aria-label="How to resolve upload errors"
                aria-haspopup="true"
                onClick={popoverClick}
              >
                <i className="bi bi-cone-striped sp-margin5" aria-hidden="true"></i>
                How to Fix
              </button>

              <OverlayPanel ref={op}>
                <div style={{ minWidth: "200px" }}>
                  <strong className="spacer-margin8">
                    How to resolve errors:
                  </strong>
                  <ol>
                    <li>Download the list by clicking on the download button.</li>
                    <li>Open download.csv file from your download folder on your computer.</li>
                    <li>Identify the errors and make your fix.</li>
                    <li>Delete the "Reason for Errors" column.</li>
                    <li>Save your file with a different name.</li>
                    <li>Go to {getUploadTypeDisplay(userObj?.uploadType)} upload page and re-upload.
                    </li>
                  </ol>
                </div>
              </OverlayPanel>
            </div>
          </div>

          {/* This is for User Upload Summary */}
          <div ref={tableContainerRef} className="drag-scroll-table">
          {userObj?.uploadType === UploadType.AddUsers ||
          userObj?.uploadType === UploadType.DeleteUsers ? (
            <div className="sp-margin3">
              <DataTable
                value={gridObj}
                ref={dt}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                tableStyle={{ minWidth: "50rem" }}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                selectionMode="single"
                columnResizeMode="expand"
                resizableColumns
                globalFilter={globalFilter}
                dataKey="_rowId"
                expandedRows={expandedRows}
                onRowToggle={onRowToggle}
                rowExpansionTemplate={rowExpansionTemplate}
                aria-label="User upload failed records"
              >
                <Column expander style={{ width: "2rem" }} />
                <Column field="UserType" header="User Type" sortable className="w-md8" ></Column>
                <Column field="NamePrefix" header="Prefix" sortable className="w-md4" ></Column>
                <Column field="FirstName" header="First Name" sortable className="w-md8" ></Column>
                <Column field="LastName" header="Last Name" sortable className="w-md8" ></Column>
                <Column field="Credential" header="Credential" sortable className="w-md4" ></Column>
                <Column field="ContactEmail" header="Contact Email" sortable ></Column>
                <Column field="UserName" header="Username" sortable className="w-md8" ></Column>
                <Column field="PhoneNumber" header="Phone Number" sortable className="w-md8" ></Column>
                <Column field="FaxNumber" header="Fax Number" sortable className="w-md8" ></Column>
                <Column field="NPI" header="NPI" sortable></Column>
                <Column field="LicenseNumber" header="License Number" sortable className="w-md8" ></Column>
                <Column field="StateofLicense" header="State of License" sortable className="w-md4" ></Column>
                <Column field="NUCCTaxonomy" header="NUCC Taxonomy" sortable ></Column>
                <Column field="Address1" header="Address 1" sortable></Column>
                <Column field="Address2" header="Address 2" sortable></Column>
                <Column field="City" header="City" sortable></Column>
                <Column field="State" header="State" sortable className="w-md8" ></Column>
                <Column field="Zipcode" header="Zip Code" sortable className="w-md8" ></Column>
                <Column field="RequestedDirectAddress" header="Requested Direct Address" sortable ></Column>
                <Column field="ValidationMessage" header="Reason for Errors" body={reasonCountBody} ></Column>
              </DataTable>
            </div>
          ) : userObj?.uploadType === UploadType.AddOrganizationEmails ||
            userObj?.uploadType === UploadType.DeleteOrganizationEmails ? (
            <div className="sp-margin3">
              <DataTable
                value={gridObj}
                ref={dt}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                tableStyle={{ minWidth: "50rem" }}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                selectionMode="single"
                columnResizeMode="expand"
                resizableColumns
                globalFilter={globalFilter}
                dataKey="_rowId"
                expandedRows={expandedRows}
                onRowToggle={onRowToggle}
                rowExpansionTemplate={rowExpansionTemplate}
                aria-label="Organization email upload failed records"
              >
                <Column expander style={{ width: "2rem" }} />
                <Column field="ExternalID" header="ExternalID" sortable ></Column>
                <Column field="RequestedOrgDirectEmail" header="RequestedOrgDirectEmail" sortable ></Column>
                <Column field="EmailPurpose" header="EmailPurpose" sortable className="w-md8" ></Column>
                {/*<Column field="Organization" header="Organization" sortable ></Column>*/}
                <Column field="ValidationMessage" header="Reason for Errors" body={reasonCountBody} ></Column>
              </DataTable>
            </div>
          ) : (
            <div className="sp-margin3">
              <DataTable
                value={gridObj}
                ref={dt}
                paginator
                rows={10}
                rowsPerPageOptions={[10, 25, 50]}
                tableStyle={{ minWidth: "50rem" }}
                paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                currentPageReportTemplate="{first} to {last} of {totalRecords}"
                selectionMode="single"
                columnResizeMode="expand"
                resizableColumns
                globalFilter={globalFilter}
                dataKey="_rowId"
                expandedRows={expandedRows}
                onRowToggle={onRowToggle}
                rowExpansionTemplate={rowExpansionTemplate}
                aria-label="Organization upload failed records"
              >
                <Column expander style={{ width: "2rem" }} />
                <Column field="OrganizationName" header="Name" sortable ></Column>
                <Column field="ExternalID" header="ExternalID" sortable ></Column>
                <Column field="Description" header="Description" sortable ></Column>
                <Column field="Keyword" header="Keyword" sortable></Column>
                <Column field="OrgKind" header="OrgKind" sortable></Column>
                <Column field="NUCCTaxonomy" header="NUCC Taxonomy" sortable ></Column>
                <Column field="NPI" header="NPI" sortable></Column>
                <Column field="Address1" header="Address 1" sortable></Column>
                <Column field="Address2" header="Address 2" sortable></Column>
                <Column field="City" header="City" sortable></Column>
                <Column field="State" header="State" sortable className="w-md8" ></Column>
                <Column field="Zipcode" header="Zip Code" sortable className="w-md8" ></Column>
                <Column field="PhoneNumber" header="Phone Number" sortable className="w-md8" ></Column>
                <Column field="FaxNumber" header="Fax Number" sortable className="w-md8" ></Column>
                <Column field="ValidationMessage" header="Reason for Errors" body={reasonCountBody} ></Column>
              </DataTable>
            </div>
          )}
          </div>
          </div>
      </section>
    </Layout>
  );
};

export default UploadSummary;
